
import { useState } from "react"
import { cn } from "@/lib/utils"
import {
  Heart,
  Store,
  GraduationCap,
  ChevronRight,
  MapPin,
  Bell,
  Share2,
  PenLine,
  BarChart3,
  Megaphone,
  Code,
  Server,
  Users,
} from "lucide-react"
import { Button } from "@/components/ui/button"

const audiences = [
  {
    id: "residents",
    label: "For Residents",
    icon: Heart,
    color: "text-amber",
    bgColor: "bg-amber/10",
    activeBg: "bg-amber",
    headline: "Never miss what matters in your neighbourhood",
    description:
      "Whether you're new to town or have lived here for decades, Corkboard keeps you connected to the pulse of your community. Discover events, support local shops, and find out what's really happening around you.",
    features: [
      {
        icon: MapPin,
        title: "Hyper-local discovery",
        description: "Find events and offers within your chosen radius, from your street to your region.",
      },
      {
        icon: Bell,
        title: "Personalised alerts",
        description: "Get notified about things that matter to you: live music, kids' activities, farmers markets.",
      },
      {
        icon: Share2,
        title: "Share with friends",
        description: "Found something great? Share events directly with friends and family.",
      },
    ],
  },
  {
    id: "retailers",
    label: "For Retailers",
    icon: Store,
    color: "text-forest",
    bgColor: "bg-forest/10",
    activeBg: "bg-forest",
    headline: "Reach your community without the complexity",
    description:
      "You shouldn't need to be a marketing expert to get local customers through your door. Corkboard gives independent businesses simple tools to share offers, promote events, and connect with nearby residents.",
    features: [
      {
        icon: PenLine,
        title: "Post in seconds",
        description: "Add offers and events through our simple web interface. Or just message our WhatsApp bot.",
      },
      {
        icon: BarChart3,
        title: "Understand what works",
        description: "See anonymised engagement insights to know which promotions resonate with your community.",
      },
      {
        icon: Megaphone,
        title: "Amplify your voice",
        description: "Your listings surface when locals search, helping you compete with online giants.",
      },
    ],
  },
  {
    id: "maintainers",
    label: "For Maintainers",
    icon: GraduationCap,
    color: "text-forest-light",
    bgColor: "bg-forest-light/10",
    activeBg: "bg-forest-light",
    headline: "Learn by doing, for your community",
    description:
      "Corkboard is more than software. It's an educational platform where local students and volunteers learn real-world tech skills (cloud infrastructure, API security, AI systems) while maintaining the platform that serves their own community.",
    features: [
      {
        icon: Code,
        title: "Hands-on workshops",
        description: "Regular sessions at libraries and community hubs to learn backend development and AI.",
      },
      {
        icon: Server,
        title: "Local-first infrastructure",
        description: "Understand how privacy-respecting, community-owned technology actually works.",
      },
      {
        icon: Users,
        title: "Build your portfolio",
        description: "Contribute to a real project that makes a difference. Great for CVs and university applications.",
      },
    ],
  },
]

export function ForEveryone() {
  const [activeTab, setActiveTab] = useState("residents")
  const activeAudience = audiences.find((a) => a.id === activeTab)!

  return (
    <section id="for-you" className="py-24 md:py-32 bg-cream">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-sm font-medium text-forest uppercase tracking-wider mb-4">
            For Everyone
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground text-balance">
            Built for every part of your community
          </h2>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {audiences.map((audience) => {
            const Icon = audience.icon
            const isActive = activeTab === audience.id
            return (
              <button
                key={audience.id}
                type="button"
                onClick={() => setActiveTab(audience.id)}
                className={cn(
                  "flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-all duration-300",
                  isActive
                    ? `${audience.activeBg} text-cream shadow-lg`
                    : `${audience.bgColor} ${audience.color} hover:shadow-md`
                )}
              >
                <Icon className="w-4 h-4" />
                {audience.label}
              </button>
            )
          })}
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left: Description */}
          <div className="space-y-6">
            <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground leading-snug">
              {activeAudience.headline}
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {activeAudience.description}
            </p>
            <Button
              variant="outline"
              className="rounded-full border-2 border-border hover:border-forest hover:text-forest transition-colors bg-transparent"
            >
              Learn more
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Right: Feature Cards */}
          <div className="space-y-4">
            {activeAudience.features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <div
                  key={feature.title}
                  className="flex gap-4 p-5 rounded-2xl bg-card border border-border/50 hover:border-border hover:shadow-md transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div
                    className={cn(
                      "w-12 h-12 rounded-xl flex items-center justify-center shrink-0",
                      activeAudience.bgColor,
                      activeAudience.color
                    )}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{feature.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
